import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { KeenIcon } from '@/components';
import { useEffect, useState } from 'react';
import { CrudAvatarUpload } from '@/partials/crud';
import { Timezones } from '@/utils'
import Select from 'react-select';
import { getGlobalSelectClass } from '@/utils';
import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl';
import { isSuperAdmin } from '@/utils';

const PersonalInfo = ({ userData, onEdit, onSaveLoading, loading }) => {
  const [isEditing, setIsEditing] = useState(false);
  const APP_URL = import.meta.env.VITE_APP_BASE_URL;
  const formattedTimezones = Timezones.map(tz => ({
    label: tz.replace(/_/g, ' '), // Optional: make label more readable
    value: tz
  }));
  const { formatMessage } = useIntl();
  const address = userData?.address?.[0] || {};

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required(formatMessage({ id: 'validation.first_name_required' })),
    last_name: Yup.string().required(formatMessage({ id: 'validation.last_name_required' })),
    email: Yup.string().email(formatMessage({ id: 'validation.invalid_email' })).required(formatMessage({ id: 'validation.email_required' })),
    // address: Yup.string().required('Address is required'),
    // city: Yup.string().required('City is required'),
    // state: Yup.string().required('State is required'),
    // zip: Yup.string().required('Zip is required'),
  });

  const initialValues = {
    first_name: userData?.first_name,
    last_name: userData?.last_name || '',
    email: userData?.email || '',
    address: address?.address || '',
    city: address?.city || '',
    state: address?.state || '',
    zip: address?.zip || '',
    avatar: userData?.profile_image ? [{ dataURL: userData.profile_image }]
      : [],
    timezone: userData?.timezone || '',
  }

  const handleResetForm = (resetForm) => {
    setIsEditing(false);
    resetForm();
  }

  return (
    <div className="min-w-full card">
      <div className="flex items-center justify-between card-header">
        <h3 className="card-title"><FormattedMessage id="user_profile.my_information" /></h3>
        <button
          onClick={() => setIsEditing(true)}
          className="btn btn-xs btn-icon btn-icon-lg link"
        >
          <KeenIcon icon="notepad-edit" className="text-[12px]" />
        </button>
      </div>

      <div className="pb-3 card-table scrollable-x-auto">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={(values, { setErrors }) => {
            const formData = new FormData();
            const avatarFile = Array.isArray(values.avatar) && values.avatar.length > 0 && values.avatar[0]?.file
              ? values.avatar[0].file
              : '';
            formData.append('first_name', values.first_name);
            formData.append('last_name', values.last_name);
            formData.append('email', values.email);
            formData.append('profile_image', avatarFile); // assuming this exists
            if (!isSuperAdmin()) {
              formData.append('address[address]', values.address);
              formData.append('address[city]', values.city);
              formData.append('address[state]', values.state);
              formData.append('address[zip]', values.zip);
            }
            formData.append('timezone', values.timezone);
            if (typeof (avatarFile) === 'string' && values.avatar === 'no_image') { formData.append('profile_image_remove', 'remove_img'); }

            formData.append('_method', 'PUT');
            // Stringify the nested address object
            // console.log('------->',values)
            // for (const [key, value] of formData.entries()) {
            //   if (value instanceof File) {
            //     console.log(`${key}:`, value.name);
            //   } else {
            //     console.log(`${key}:`, value);
            //   }
            // }
            // return
            onEdit(formData, setErrors);
            setIsEditing(false);
          }}
        >
          {({ values, setFieldValue, resetForm }) => (
            <Form>
              <table className="table text-sm text-gray-500 align-middle">
                <tbody>
                  {loading || onSaveLoading ? (
                    <tr>
                      <td colSpan={2} className="py-4 text-center">
                        <span className=""><FormattedMessage id="user_profile.loading" /></span>
                      </td>
                    </tr>
                  ) : (
                    <>
                      <tr>
                        <td className="font-medium text-gray-800"><FormattedMessage id="user_profile.personal_info" /></td>
                        <td colSpan={2}></td>
                      </tr>

                      <tr>
                        {isEditing && (
                          <>
                            <td className="py-2 text-gray-600">Photo</td>
                            <td className="py-2 text-gray-600">
                              <CrudAvatarUpload
                                value={values.avatar}
                                onChange={(avatar) => {
                                  setFieldValue('avatar', avatar);
                                }}
                              />
                              <span className='block text-xs'><FormattedMessage id="user_profile.recommended_ratio" /></span>
                            </td>
                          </>
                        )
                        }
                      </tr>

                      <tr>
                        <td className="py-2 text-gray-600"><FormattedMessage id="user_profile.name" /> {isEditing && <span className='pl-px text-red-600'>*</span>}</td>
                        <td className="py-2 text-gray-800">
                          {isEditing ? (
                            <>
                              <div className="flex items-center gap-5">
                                <div className='w-full'>
                                  <Field
                                    name="first_name"
                                    placeholder="First Name"
                                    className="w-full input input-sm input-bordered"
                                  />
                                  <ErrorMessage
                                    name="first_name"
                                    component="div"
                                    className="mt-1 text-xs text-red-500"
                                  />
                                </div>
                                <div className='w-full'>
                                  <Field
                                    name="last_name"
                                    placeholder="Last Name"
                                    className="w-full input input-sm input-bordered"
                                  />
                                  <ErrorMessage
                                    name="last_name"
                                    component="div"
                                    className="mt-1 text-xs text-red-500"
                                  />
                                </div>
                              </div>
                            </>
                          ) : (
                            userData?.first_name + " " + userData?.last_name
                          )}
                        </td>
                      </tr>

                      <tr>
                        <td className="py-2 text-gray-600"><FormattedMessage id="user_profile.email" /> </td>
                        <td className="py-2 text-gray-800">
                          {isEditing ? (
                            <>
                              <Field
                                name="email"
                                disabled={userData?.email}
                                className="w-full input input-sm input-bordered"
                              />
                              <ErrorMessage
                                name="email"
                                component="div"
                                className="mt-1 text-xs text-red-500"
                              />
                            </>
                          ) : (
                            userData?.email
                          )}
                        </td>
                      </tr>

                      <tr>
                        <td className="py-2 text-gray-600"><FormattedMessage id="user_profile.two_fa" /></td>
                        <td className="py-2 text-gray-800">
                          {/* Both states link to Security, where 2FA is actually
                              turned on or off. The badge used to be plain text,
                              so "To be set" told people something needed doing
                              without offering any way to do it. */}
                          <Link
                            to="/account/security/overview"
                            className="inline-flex hover:opacity-80"
                          >
                            {userData?.is_2fa_enabled === 1 ? (
                              <span className="badge badge-sm badge-outline badge-success">
                                <FormattedMessage id="user_profile.enabled" />
                              </span>
                            ) : (
                              <span className="badge badge-sm badge-outline"><FormattedMessage id="user_profile.to_be_set" /></span>
                            )}
                          </Link>
                        </td>
                      </tr>

                      <tr>
                        <td className="py-2 text-gray-600"><FormattedMessage id="user_profile.timezone" /></td>
                        <td className="py-2 text-gray-800">
                          {isEditing ? (
                            <Select
                              name="timezone"
                              options={formattedTimezones}
                              value={formattedTimezones.find(option => option.value === values.timezone)}
                              onChange={selected => setFieldValue('timezone', selected.value)}
                              isSearchable
                              className="w-full p-0 input react-select-container"
                              classNamePrefix="react-select"
                              classNames={getGlobalSelectClass()}
                            />
                          ) : (
                            userData?.timezone
                          )}
                        </td>
                      </tr>

                      {!isSuperAdmin() && (
                        <>
                          <tr>
                            <td className="font-medium text-gray-800"><FormattedMessage id="user_profile.address_info" /></td>
                            <td colSpan={2}></td>
                          </tr>
                          {['address', 'city', 'state', 'zip'].map((field) => (
                            <tr key={field}>
                              <td className="py-2 text-gray-600 capitalize">{field}</td>
                              <td className="py-2 text-gray-800">
                                {isEditing ? (
                                  <>
                                    <Field
                                      name={field}
                                      className="w-full input input-sm input-bordered"
                                    />
                                    <ErrorMessage
                                      name={field}
                                      component="div"
                                      className="mt-1 text-xs text-red-500"
                                    />
                                  </>
                                ) : (
                                  address?.[field] || "—"
                                )}
                              </td>
                            </tr>
                          ))}
                        </>
                      )}

                      {isEditing && (
                        <tr>
                          <td></td>
                          <td className="pt-3">
                            <button
                              type="submit"
                              disabled={onSaveLoading}
                              className="btn btn-sm btn-primary"
                            >
                              {onSaveLoading ? "Saving..." : <FormattedMessage id="user_profile.save" />}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleResetForm(resetForm)}
                              className="btn btn-sm "
                            >
                              <FormattedMessage id="user_profile.cancel" />
                            </button>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export { PersonalInfo };